import hyperdom from 'hyperdom'
import {capsCase} from 'root-core/utils/text/case.js'
import generateDOB from '../../../utils/generateDOB.js'
import parseUserSuppliedDate from '../../../parseUserSuppliedDate.js'
import PassengersDetailsSchema from './passengersDetailsSchema.js'
import titles from '../../../../../../config/resources/titles.js'
import LocalDate from '@booking-services/localDate'
import ValidationBase from '../../validationBase.jsx'
import formatDate from 'root-packages/dates/formatDate.js'
import customSelect from '../../../customSelect.jsx'
import responsiveRender from '../../../responsiveRender.js'
import DateOfBirthPicker from '../../../components/dateOfBirthPicker.jsx'
import ReferralSchema from './referralSchema.js'
import assetPath from '../../../assetPath.js'
import formatPrice from '../../../packages/ui-helpers/formatPrice.js'
import { getRandomName } from '../../../utils/fakeData.js'
import {scrollToAndFocus} from '../../../../booking-engine/components/materialDesign/scrollToAndFocus.js'

export default class PassengersDetails extends ValidationBase {
  constructor ({
    api,
    browserCapabilities,
    config,
    countries,
    departure,
    dmpg,
    quoteCriteria,
    summary,
    showLoading,
    hideLoading,
  }) {
    super()
    this.api = api
    this.browserCapabilities = browserCapabilities
    this.config = config
    this.countries = countries
    this.departure = departure
    this.dmpg = dmpg
    this.quoteCriteria = quoteCriteria
    this.schema = new PassengersDetailsSchema({
      hasFlights: () => this.quoteCriteria.hasFlights(),
      hasPromisedFlights: () => this.quoteCriteria.hasPromisedFlights(),
      config: this.config,
      departure: this.departure,
    }).buildSchema()
    this.referralSchema = new ReferralSchema({
      config: this.config,
    }).buildSchema()
    this.isDobUpdated = false
    this.summary = summary
    this.showLoading = showLoading
    this.hideLoading = hideLoading

    if (config.environment === 'test') {
      window.fillInWithTestData = (data) => this.fillInWithTestData(data)
    }
  }

  onload () {
    return this.changeCountry()
  }

  async changeCountry () {
    this.showLoading()
    const country = this.quoteCriteria.leadPassenger && this.quoteCriteria.leadPassenger.address.country
    this.regionsCountry = country

    try {
      this.regions = await this.api.regions(country)
    } catch (e) {
      this.regions = []
    }
    this.hideLoading()
    this.refresh()
  }

  regionChanged () {
    this.quoteCriteria.leadPassenger.ontarioResident = this.quoteCriteria.leadPassenger.address.region === 'Ontario'
  }

  zipcodePlaceholder (address) {
    return address.country === 'United States Of America' ? 'Zip' : 'Zip/Postal Code'
  }

  titleChanged (passenger) {
    passenger.gender = this.titleGender(passenger)
  }

  titleGender (passenger) {
    const title = titles.find(t => t.name == passenger.title)
    return title && title.gender
  }

  fillInWithTestData (data = []) {
    const leadTestData = data[0] || {}
    const leadPassenger = this.quoteCriteria.passengers[0]
    leadPassenger.title = leadTestData.title
    this.titleChanged(leadPassenger)
    if (leadPassenger.gender === null) {
      leadPassenger.gender = leadTestData.gender
    }
    leadPassenger.firstName = leadTestData.firstName
    leadPassenger.lastName = leadTestData.lastName
    if (leadTestData.middleName) {
      leadPassenger.middleName = leadTestData.middleName
    }
    leadPassenger.emailAddress = leadTestData.emailAddress
    leadPassenger.address = {
      ...leadPassenger.address,
      ...leadTestData.address,
    }
    this.regionChanged()
    if (leadTestData.dateOfBirth) {
      leadPassenger.dateOfBirth = formatDate(leadTestData.dateOfBirth, 'MM/DD/YYYY')
    }
    if (leadTestData.phoneNumber?.number) {
      leadPassenger.phoneNumber = {
        ...leadPassenger.phoneNumber,
        ...leadTestData.phoneNumber,
      }
    }
    if (leadPassenger.phoneNumber.internationalCode) {
      leadPassenger.phoneNumber.countryCode = Object.values(this.countries()).find(country => country.internationalCallingCode === leadPassenger.phoneNumber.internationalCode).alpha2
    }
    for (let i = 1; i< this.quoteCriteria.passengers.length; i++) {
      const paxTestData = data[i] || {}
      const passenger = this.quoteCriteria.passengers[i]
      if (paxTestData.middleName) {
        passenger.middleName = paxTestData.middleName
      }
      passenger.firstName = paxTestData.firstName
      passenger.lastName = paxTestData.lastName
      passenger.title = paxTestData.title
      this.titleChanged(passenger)
      if (passenger.gender === null) {
        passenger.gender = paxTestData.gender
      }
      if (passenger.type !== 'child' && paxTestData.dateOfBirth) {
        passenger.dateOfBirth = formatDate(paxTestData.dateOfBirth, 'MM/DD/YYYY')
      }
    }
  }

  generateRandomPassenger () {
    const address = {
      line1: '1013 Weda Cir',
      city: 'Mayfield',
      country: 'United States Of America',
      region: 'Kentucky',
      postcode: '99999',
    }

    const phoneNumber = {
      countryCode: 'US',
      internationalCode: '+1',
      areaCode: '555',
      number: '555' + (Math.floor(1000 + Math.random() * 9000)).toString().padStart(4,'0'),
    }

    const passenger = {
      title: 'Mr',
      ...getRandomName(),
      get emailAddress() {
        return `${this.firstName.toLowerCase()}.${this.lastName.toLowerCase()}@somedomain.com`
      },
      dateOfBirth: generateDOB({
        departureStartDate: this.departure.startDate.date,
        ageFrom: this.departure.adultAgeFrom,
        ageTo: this.departure.adultAgeTo,
        dateFormat: this.config.dateFormat
      }),
      address,
      phoneNumber,
    }

    return passenger
  }

  fillInWithFakeData () {
    const leadPassenger = this.quoteCriteria.passengers[0]
    const shouldFillDOB = this.config.features.ageRestriction || this.quoteCriteria.hasFlights() || this.quoteCriteria.hasPromisedFlights() || this.departure.hasInternalFlights
    const { title, firstName, middleName, lastName, emailAddress, dateOfBirth, address, phoneNumber } = this.generateRandomPassenger()
    leadPassenger.title = title
    this.titleChanged(leadPassenger)
    leadPassenger.firstName = firstName
    leadPassenger.middleName = middleName
    leadPassenger.lastName = lastName
    leadPassenger.emailAddress = emailAddress

    if (shouldFillDOB) {
      leadPassenger.dateOfBirth = dateOfBirth
    }

    leadPassenger.address = address
    leadPassenger.phoneNumber = phoneNumber

    this.changeCountry()

    this.fillInAdditionalData({
      passenger: leadPassenger,
      isLeadPassenger: true,
    })

    for (let i = 1; i< this.quoteCriteria.passengers.length; i++) {
      const passenger = this.quoteCriteria.passengers[i]
      const { title, firstName, middleName, lastName, dateOfBirth } = this.generateRandomPassenger()
      passenger.firstName = firstName
      passenger.middleName = middleName
      passenger.lastName = lastName
      passenger.title = title
      this.titleChanged(passenger)

      if (passenger.type === 'adult' && shouldFillDOB) {
        passenger.dateOfBirth = dateOfBirth
      }

      this.fillInAdditionalData({
        passenger,
        isLeadPassenger: false,
      })
    }
  }

  fillInAdditionalData () {}

  allChildrenOldEnoughForTourAsOfToday () {
    if (this.quoteCriteria.passengers.filter(p => p.type === 'child').some(p => {
      const dateOfBirth = parseUserSuppliedDate(p.dateOfBirth, this.config.dateFormat)
      const ageMS = new LocalDate().toDate().getTime() - dateOfBirth
      const age = new Date()
      age.setTime(ageMS)
      return age.getFullYear() - 1970 < this.departure.childAgeFrom
    })) {
      return `There was an error while trying to put your tour on request. Please give us a call on ${this.config.phone.general}`
    }
    return void 0
  }

  renderPassportInformationText () {
    const passportInformationText = this.config.passportInformationText
    return passportInformationText
      ? <span className='passenger-details__passport-information'>{passportInformationText}</span>
      : undefined
  }

  render () {
    return <div>
      {this.quoteCriteria.passengers.map((passenger, index) => {
        const isLeadPassenger = index === 0
        const shouldRenderBirthDatePicker = passenger.type === 'child' || this.config.features.ageRestriction || this.hasFlights()

        return [
          <div class="passenger-details-header">
            {`Passenger ${index+1} (${isLeadPassenger ? 'Lead/' : ''}${capsCase(passenger.type)})`}
            {isLeadPassenger
              ? <span class="passenger-details-header__lead-description">Person responsible for booking</span>
              : undefined
            }
          </div>,
          <div id={`passenger${index+1}Details`} className={['o-accent-box', 'passenger-detail']}>
            <h3 className="form-item-header">Personal details</h3>
            <div className='o-form-item__container'>
              {this.renderPassportInformationText()}
              <div className='form-item-title'>
                {this.renderSelectField({
                  autocomplete: 'honorific-prefix',
                  binding: [passenger, 'title', () => this.titleChanged(passenger)],
                  id: `title-select-${index}`,
                  label: 'Title*',
                  name: 'title',
                  options: [
                    (passenger.title ? undefined : <option value=''></option>),
                    ...titles.map(title => <option value={title.name}>{title.name}</option>),
                  ],
                })}
                {this.renderValidationError(passenger, 'title')}
              </div>
              <div className='form-item-first-name'>
                {this.renderTextField({
                  autocomplete: 'given-name',
                  binding: [passenger, 'firstName'],
                  id: `pax-first-name-${index}`,
                  label: 'First/Given name (as in passport)*',
                  name: 'firstName',
                })}
                {this.renderValidationError(passenger, 'firstName')}
              </div>

              <div className='form-item-middle-name'>
                {this.renderTextField({
                  autocomplete: 'additional-name',
                  binding: [passenger, 'middleName'],
                  id: `pax-middle-name-${index}`,
                  label: 'Middle name/Initials (as in passport)',
                  name: 'middleName',
                })}
                {this.renderValidationError(passenger, 'middleName')}
              </div>

              <div className='form-item-surname'>
                {this.renderTextField({
                  autocomplete: 'family-name',
                  binding: [passenger, 'lastName'],
                  id: `pax-surname-${index}`,
                  label: 'Surname(s) (as in passport)*',
                  name: 'lastName',
                  onblur: () => {
                    const isValid = this.isFieldValid(passenger, 'lastName')
                    isValid && !shouldRenderBirthDatePicker && this.scrollToAndFocus(isLeadPassenger
                      ? '#emailAddress'
                      : this.quoteCriteria.passengers.length === index + 1
                        ? '.discounts-offers-section'
                        : `#pax-first-name-${index + 1}`
                    )
                  },
                })}
                { this.renderValidationError(passenger, 'lastName') }
              </div>
              {
                shouldRenderBirthDatePicker
                  ? <div className='form-date-of-birth-field'>
                    { this.renderBirthDatePicker(passenger, index) }
                    { this.renderValidationError(passenger, 'dateOfBirth') }
                  </div>
                  : undefined
              }
              {
                passenger.title && !this.titleGender(passenger)
                  ? <div className='margin-top-medium form-item-gender'>
                    <div className='o-inline-short-title'>
                      <span className='title'>Gender:</span>
                    </div>
                    <div className='u-inbl -middle u-padding-right-medium'>
                      <label className='o-choice-item male'>
                        <input name={`gender${index}`} type='radio' className='o-choice-item__radio' binding={[passenger, 'gender']} value='Male' required />
                        <div className='o-choice-item__content'>
                          Male
                        </div>
                      </label>
                    </div>

                    <div className='u-inbl -middle'>
                      <label className='o-choice-item female'>
                        <input name={`gender${index}`} type='radio' className='o-choice-item__radio' binding={[passenger, 'gender']} value='Female' required />
                        <div className='o-choice-item__content'>
                          Female
                        </div>
                      </label>
                    </div>

                    { this.renderValidationError(passenger, 'gender') }
                  </div>
                  : undefined
              }
            </div>
          </div>,
          ...(isLeadPassenger ? [
            this.renderContactDetails(),
            this.renderBillingDetails(),
          ] : []),
        ]
      })}
    </div>
  }

  renderBirthDatePicker (passenger, index) {
    return <div>
      {
        new DateOfBirthPicker({
          placeholder: `Date of birth${this.config.features.dobRequired ? '*' : ''}`,
          binding: {
            scrollTo: () => {
              this.scrollToAndFocus(
                '.discounts-offers-section'
              )
            },
            get: () => passenger.dateOfBirth,
            set: (value) => {
              passenger.dateOfBirth = value
              this.fieldVisited(passenger, 'dateOfBirth', `pax-date-of-birth-${index}`)
            },
            fieldVisited: () => this.fieldVisited(passenger, 'dateOfBirth', `pax-date-of-birth-${index}`),
            setIsDobUpdated: (value) => this.isDobUpdated = value,
            isDobUpdated: () => this.isDobUpdated,
          },
          isFieldInvalid: () => this.isFieldInvalid(passenger, 'dateOfBirth'),
          config: this.config,
          isDisabled: passenger.type === 'child',
        })
      }
    </div>
  }

  renderAdvocateTooltip () {
    return <p className="passengers__referrals-advocate--tooltip">
      This field cannot be changed as it is associated with Advocate Discount. If you want to update, please remove Advocate Discount first.
    </p>
  }

  renderContactDetails () {
    const renderEmailLabelPlaceholder = (placeholder) => {
      const {clientAddressMandatory, contactDetails} = this.config.features
      const isMandatory = clientAddressMandatory || contactDetails.clientEmailAddressMandatory

      return `${placeholder}${isMandatory ? '*' : ''}`
    }

    const {leadPassenger} = this.quoteCriteria
    const countries = this.countries()
    const emailField = <div className='form-item-email-address'>
      {this.renderTextField({
        autocomplete: 'email',
        binding: [leadPassenger, 'emailAddress'],
        dataLayerId: 'pax-email-address',
        id: `emailAddress`,
        label: renderEmailLabelPlaceholder('Email address'),
        onblur: (event) => {
          event.target.value = event.target.value.trim()
          const isValid = this.isFieldValid(leadPassenger, 'emailAddress')
          isValid && this.scrollToAndFocus('#number')
        },
        type: this.browserCapabilities.input.email,
        isDisabled: !!this.quoteCriteria?.referral?.creditsToRedeem,
      })}
      {this.renderValidationError(leadPassenger, 'emailAddress', 'emailAddress')}
      {this.renderAdvocateTooltip()}
    </div>
    const phoneFields = <div className='form-item-telephone'>
      <div className='form-item-country-code'>
        <label htmlFor='pax-phone-country-code' className='u-visually-hidden'>Country Code</label>
        {customSelect({
          classes: {
            wrapper: 'pax-phone-country-code-select',
          },
          inputBinding: {
            get: () => leadPassenger.phoneNumber.countryCode
              ? countries[leadPassenger.phoneNumber.countryCode.toLowerCase()].internationalCallingCode
              : undefined,
            set: () => {},
          },
          selectBinding: [leadPassenger.phoneNumber, 'countryCode', () => {
            document.querySelector('#number').focus(),
            this.refresh()
          }],
          selectId: 'pax-phone-country-code',
          selectOnBlur: () => this.fieldVisited(leadPassenger.phoneNumber, 'countryCode', 'pax-phone-country-code'),
          options: Object.keys(countries).map(countryCode => {
            const country = countries[countryCode]
            return {
              value: countryCode.toUpperCase(),
              display: `${country.name} (${country.internationalCallingCode})`,
            }
          }),
        })}
        {this.renderValidationError(leadPassenger.phoneNumber, 'internationalCode')}
      </div>
      <div className='form-item-phone-number'>
        {this.renderTextField({
          autocomplete: 'tel-national',
          binding: {
            get: () => {
              const phoneNumber = leadPassenger.phoneNumber
              let number = (phoneNumber.areaCode) ? phoneNumber.areaCode : ''
              number += (phoneNumber.number) ? new String(phoneNumber.number) : ''
              return number
            },
            set: (value) => {
              const areaCode = value.substring(0, 3)
              const number = value.substring(3)
              leadPassenger.phoneNumber.areaCode = areaCode
              leadPassenger.phoneNumber.number = number
            },
          },
          dataLayerId: 'pax-phone-number',
          id: `number`,
          label: `Phone number${this.config.features.contactDetails.clientPhoneNumberMandatory ? '*' : ''}`,
          modelBinding: [leadPassenger.phoneNumber, 'number'],
          name: 'number',
          onblur: () => {
            const isValid = this.isFieldValid(leadPassenger.phoneNumber, 'number')
            isValid && this.scrollToAndFocus('#city')
          },
        })}
        {this.renderValidationError(leadPassenger.phoneNumber, 'number')}
      </div>
    </div>
    return <div className='o-accent-box' id='lead-passenger__contact-details'>
      <h3 className="form-item-header">Contact Details</h3>
      <div className="o-form-item__container" id='leadPassengerContact'>
        {responsiveRender('small',
          () => [emailField, phoneFields],
          () => [phoneFields, emailField]
        )}
      </div>
      {this.renderReferral()}
      {this.renderSmsInitialTransactionalConsent()}
    </div>
  }

  renderReferral () {
    const {referral} = this.quoteCriteria
    const toggleReferralAdvocate = (self) => {
      self.classList.toggle('active')
      if(self.classList.contains('active')) {
        self.innerText = "Hide"
      } else {
        self.innerText = "I have referral credits"
      }
    }

    const checkAdvocate = async (ev) => {
      ev.preventDefault()
      const {valid: isValid} = this.validateReferral()
      if (isValid) {
        const data = await this.summary.getReferralAdvocate()
        this.advocate = data.advocate
        this.promotion = data.promotion
        referral.advocateEmail = this.advocate.advocateEmail
      }
    }

    const selectPassengerForDiscount = (passengerIndex) => {
      if(referral.advocateEmail !== this.quoteCriteria.passengers[0].emailAddress) {
        referral.passengerSelectedForPromotion = null
        referral.advocateMessage = `Advocate email has been changed. Please check the balance again`
        return
      }

      referral.advocateMessage = undefined
      this.summary.applyReferralAdvocate( this.advocate, this.promotion, passengerIndex)
    }

    return this.config.features.referrals.advocateSavingsEnabled ? (<div className="passengers__referrals">
      <p
        className="passengers__referrals-advocate--trigger"
        onclick={(ev) => toggleReferralAdvocate(ev.target)}
      >I have referral credits</p>
      <div className="passengers__referrals-advocate--form">
        {
          referral.advocateMessage ?
            <div className="passengers__referrals-advocate--error message-warning">
              <span>{referral.advocateMessage}</span>
            </div> :
            undefined
        }
        <p className="passengers__referrals-advocate--info">Please enter your Email address and Advocate Code to check the balance and apply your credits.</p>
        <div className='passengers__referrals-advocate--input'>
          {this.renderTextField({
            binding: [referral, 'advocateId'],
            dataLayerId: 'referral-advocate-id',
            id: `referralAdvocateId`,
            label: 'Advocate Code',
            name: 'referralAdvocateId',
            onblur: (event) => {
              event.target.value = event.target.value.trim()
              const isValid = this.isFieldValid(referral, 'advocateId')
              isValid && this.scrollToAndFocus('#referralAdvocateId')
            },
            type: 'text',
            isDisabled: !!this?.quoteCriteria?.referral?.creditsToRedeem,
          })}
          {this.renderAdvocateTooltip()}
          {this.renderValidationError(referral, 'advocateId', 'emailAddress')}
        </div>
        <button className="passengers__referrals-advocate--submit btn-secondary-outline" type="button" onclick={async (ev) => await checkAdvocate(ev)}>check balance</button>
        {referral.availableCredits ?
          <div className="passengers__referrals-advocate--details">
            <h1 className="passengers__referrals-advocate--details-header">{`You have a ${ formatPrice({amount: referral.availableCredits, currency: this.departure.prices.currency, allowZero: true}) } Advocate Discount available.`}</h1>
            <p className="passengers__referrals-advocate--details-text">Select the passenger you wish to give a discount to:</p>
            <div className='passengers__referrals-advocate--select'>
              {this.quoteCriteria.passengers.map((passenger, index) => {
                return <div className='passengers__referrals-advocate--select-passenger'>
                  <label className='o-choice-item'>
                    <input
                      name='passenger-referral'
                      type='radio'
                      className='o-choice-item__radio'
                      binding={[referral, 'passengerSelectedForPromotion']}
                      onclick={() => selectPassengerForDiscount(index)}
                      value={passenger}
                    />
                    <div className='o-choice-item__content'>
                      {passenger.firstName && passenger.lastName ?
                        `${passenger.firstName} ${passenger.lastName} ${index === 0 ? "(Lead Passenger)" : ""}` :
                        `Passenger ${index + 1}`
                      }
                    </div>
                  </label>
                </div>
              })}
            </div>
            {
              this.quoteCriteria.promotions.find(promo => promo.promotionSubtype === "ADV") ? <div className='passengers__referrals-advocate--added'>
                <img
                  src={assetPath('public/img/checked.svg')}
                  className='passengers__referrals-advocate--added-icon'/>
                <span className="passengers__referrals-advocate--added-text">The Advocate discount applied succesfully</span>
              </div> : undefined
            }
          </div> : undefined
        }
      </div>
    </div>) : undefined
  }

  renderBillingDetails () {
    const renderAddressMandatoryPlaceholder = (placeholder) => {
      return `${placeholder}${this.config.features.clientAddressMandatory ? '*' : ''}`
    }
    const {leadPassenger} = this.quoteCriteria
    const countries = this.countries()
    return <div className='o-accent-box' id='lead-passenger__billing-details'>
      <h3 className="form-item-header">Billing Details</h3>
      <div className="o-form-item__container" id='leadPassengerAddress'>
        <div className='form-item-country'>
          {this.renderSelectField({
            autocomplete: 'country-name',
            binding: [leadPassenger.address, 'country', () => this.changeCountry()],
            dataLayerId: 'pax-country',
            id: 'country',
            label: renderAddressMandatoryPlaceholder('Country / Region'),
            name: 'country',
            options: [
              (leadPassenger.address.country ? <option disabled hidden value=''>Country</option> : <option value=''></option>),
              ...Object.values(countries).map(country => <option value={country.name}>{country.name}</option>),
            ],
          })}
          {this.renderValidationError(leadPassenger.address, 'country')}
        </div>
        <div className='form-item-address-city'>
          {this.renderTextField({
            autocomplete: 'address-level2',
            binding: [leadPassenger.address, 'city'],
            dataLayerId: 'pax-city',
            id: `city`,
            label: renderAddressMandatoryPlaceholder(this.config.localisedWords.city),
            name: 'city',
          })}
          {this.renderValidationError(leadPassenger.address, 'city')}
        </div>
        <div className='form-item-address-line1'>
          {this.renderTextField({
            autocomplete: 'address-line1',
            binding: [leadPassenger.address, 'line1'],
            dataLayerId: 'pax-address-line-1',
            id: `addressLine1`,
            label: renderAddressMandatoryPlaceholder('Address Line 1'),
            name: 'addressLine1',
          })}
          { this.renderValidationError(leadPassenger.address, 'line1') }
        </div>

        <div className='form-item-address-line2'>
          {this.renderTextField({
            autocomplete: 'address-line2',
            binding: [leadPassenger.address, 'line2'],
            dataLayerId: 'pax-address-line-2',
            id: `addressLine2`,
            label: 'Address Line 2',
            name: 'addressLine2',
          })}
          { this.renderValidationError(leadPassenger.address, 'line2') }
        </div>

        <div className='form-item-region'>
          { this.regions && this.regions.length
            ? this.renderSelectField({
              autocomplete: 'address-level1',
              binding: [leadPassenger.address, 'region'],
              dataLayerId: 'pax-region-select',
              id: 'region',
              label: renderAddressMandatoryPlaceholder(this.config.localisedWords.region),
              name: 'region',
              onchange: () => this.regionChanged(),
              options: [
                (leadPassenger.address.region ? undefined : <option value=''></option>),
                ...this.regions.map(region => <option value={region}>{region}</option>),
              ],
            })
            : this.renderTextField({
              autocomplete: 'address-level1',
              binding: [leadPassenger.address, 'region'],
              dataLayerId: 'pax-region-input',
              id: `region`,
              label: renderAddressMandatoryPlaceholder(this.config.localisedWords.region),
              name: 'region',
            })
          }
          { this.renderValidationError(leadPassenger.address, 'region') }
        </div>

        <div className='form-item-postcode'>
          {this.renderTextField({
            autocomplete: 'postal-code',
            binding: [leadPassenger.address, 'postcode'],
            dataLayerId: 'pax-postcode',
            id: `postCode`,
            label: renderAddressMandatoryPlaceholder(this.zipcodePlaceholder(leadPassenger.address)),
            name: 'postCode',
            onblur: () => {
              const isValid = this.isFieldValid(leadPassenger.address, 'postcode')
              isValid && this.scrollToAndFocus(this.quoteCriteria.passengers.length > 1 ? '#pax-first-name-1' : '.discounts-offers-section')
            },
          })}
          { this.renderValidationError(leadPassenger.address, 'postcode', 'pax-postcode') }
        </div>
      </div>
    </div>
  }

  submit () {
    const country = this.countries()[this.quoteCriteria.leadPassenger.phoneNumber.countryCode.toLowerCase()] || {}
    this.quoteCriteria.leadPassenger.phoneNumber.internationalCode = country.internationalCallingCode || this.config.internationalCode

    this.resetScrolledToError()
    if (!this.validate().valid) {
      return false
    }
    return true
  }

  hasFlights () {
    return this.quoteCriteria.hasFlights() || this.quoteCriteria.hasPromisedFlights() || this.departure.hasInternalFlights
  }

  fieldVisited (model, property, fieldId) {
    super.fieldVisited(model, property)
    this.dmpg.sendImmediately({
      event: 'passengerFormFieldUpdated',
      fieldId: fieldId,
      isValid: this.isFieldValid(model, property),
    })
  }

  renderSelectField ({
    binding,
    dataLayerId,
    id,
    label,
    name,
    onchange = () => {},
    options,
  } = {}) {
    const [model, property] = binding
    return <label for={id}
      className={[
        'material-field-outlined',
        'material-select-outlined',
        {
          'visited-invalid': this.isFieldInvalid(model, property),
        },
      ]}
    >
      <select
        id={id}
        binding={binding}
        onblur={() => this.fieldVisited(model, property, dataLayerId || id)}
        onchange={onchange}
        name={name}
        required
      >
        {options}
      </select>
      <span>{label}</span>
    </label>
  }

  renderTextField ({
    binding,
    dataLayerId,
    id,
    label,
    modelBinding,
    name,
    onblur,
    type,
    isDisabled = false,
  } = {}) {
    const [model, property] = modelBinding || binding
    return <label for={id}
      className={[
        'material-field-outlined',
        {
          'visited-invalid': this.isFieldInvalid(model, property),
        },
        {
          'disabled': isDisabled,
        },
      ]}
    >
      <input
        id={id}
        type={type || 'text'}
        placeholder=' '
        binding={binding}
        onblur={(event) => {
          this.fieldVisited(model, property, dataLayerId || id)
          onblur && onblur(event)
        }}
        onfocus={(event) => {
          this.scrollIntoViewportCenter(event.target)
        }}
        name={name}
        disabled={isDisabled}
      />
      <span>{label}</span>
    </label>
  }

  renderSmsInitialTransactionalConsent () {
    if(!this.config.features.smsConsentEnabled) {
      return
    }
    const {leadPassenger} = this.quoteCriteria

    return (
      <label className='o-choice-item -fullWidth'>
        <input type='checkbox' binding={[leadPassenger, 'smsInitialTransactionalConsent']} class='o-choice-item__checkbox' name='smsInitialTransactionalConsent' id='smsInitialTransactionalConsent' />
        <div className='o-choice-item__content'>
          <span className='passenger-name'>
            By checking this box, you are providing your consent to receive SMS from Contiki Alerts. Messages and data rates may apply. Those SMS messages include booking reminders and updates. You may receive alerts until you choose to opt out of this service by replying STOP to any of our messages. Reply with HELP to any of our messages for assistance. Please see our <a href={this.config.brandPages.smsTerms} class='passenger-name-semibold' target='_blank'>SMS Terms and Conditions</a> and <a href={this.config.brandPages.resourcesPrivacyPolicy} class='passenger-name-semibold' target='_blank'>Privacy Policy</a>
          </span>
        </div>
      </label>
    )
  }

  scrollToAndFocus (cssSelector) {
    if (!this.config.isMobile) return
    scrollToAndFocus(cssSelector)
  }

  scrollIntoViewportCenter (element) {
    if (!this.config.isMobile) return
    element && setTimeout(() => {
      element.scrollIntoView({behavior: 'smooth', block: 'center'})
    }, 500)
  }
}
